/**
 * Responsible for creating a container around a field of elements with the
 * intention of applying a single error message.
 * @module elements/fields/field-container
 */
import $ from 'jquery';
import registerJQueryPlugin, { noopActions } from '../../lib/register-jquery-plugin';

const FIELD_CONTAINER_DEFAULTS = {
  errorMessage: '',
  blockName: 'fixed-label',
  labelElement: 'label',
  errorElement: 'error',
  labelErrorModifier: 'invalid',
};

const NS = 'field-container';

/**
 * Responsible for creating an error element.
 * @param {string} className - Class name of the container
 * @param {Object} data - Any data for the element
 */
const createErrorElement = (className, { message }) =>
  `<p class="${className}">${message}</p>`;

/**
 * Responsible managing an error around a container of elements.
 * @param {HTMLElement} el - Acts as the container
 * @param {Object} options - Configurable options
 */
export default function fieldContainer(el, options = {}, { deregister } = noopActions) {
  const $el = $(el);
  const {
    errorMessage,
    blockName,
    labelElement,
    errorElement,
    labelErrorModifier,
  } = Object.assign({}, FIELD_CONTAINER_DEFAULTS, options, $el.data());

  const errorClassName = `${blockName}__${errorElement}`;
  const errorSelector = `.${errorClassName}`;
  const label = `${blockName}__${labelElement}`;
  const labelError = `${blockName}__${labelElement}--${labelErrorModifier}`;

  $el
    // Since we are providing our own way of handling the error message, just in
    // case a floating error message is applied, do not use it in favor of our
    // custom way of handling an error
    .attr('data-disable-floating-error', true)
    .on(`mirror-field:error.${NS}`, (e) => {
      const { validationMessage } = e.originalEvent.detail;

      $el.find(`.${label}`).addClass(labelError);
      const $error = $el.find(errorSelector);
      const message = errorMessage || validationMessage;

      if ($error.length) {
        $error.text(message);
      } else {
        $el.append(createErrorElement(errorClassName, { message }));
      }
    })
    .on(`mirror-field:reset.${NS}`, () => {
      $el.find(`.${label}`).removeClass(labelError);
      $el.find(errorSelector).remove();
    });

  return {
    removeListeners: () => {
      deregister();
      $el.off(`.${NS}`);
    },
  };
}

registerJQueryPlugin('fieldLabelContainer', fieldContainer);


// WEBPACK FOOTER //
// ./styleguide/src/assets/elements/fields/field-container.js